<template>
    <div class="rounded-lg bg-dark-light px-4 py-3 shadow-inner" :class="wrapperClass">
        <div v-if="label" class="mb-2 text-left text-xxs font-bold uppercase tracking-widest text-white">
            {{ label }}
        </div>
        <div class="flex items-center justify-evenly space-x-3">
            <DurationField v-model="hours" suffix="h" />
            <div class="h-8 w-px bg-dark-lighter/60" />
            <DurationField v-model="minutes" suffix="min" :max="59" />
            <div class="h-8 w-px bg-dark-lighter/60" />
            <DurationField v-model="seconds" suffix="s" :max="59" />
        </div>
    </div>
</template>

<script setup>
import { computed, defineComponent, h } from 'vue'

const props = defineProps({
    // modelValue is total seconds
    modelValue: { type: Number, default: 0 },
    label: { type: String, default: '' },
    wrapperClass: { type: [String, Array, Object], default: '' },
})
const emits = defineEmits(['update:modelValue'])

const clampNumber = (value, { min = 0, max = Infinity } = {}) => {
    const parsed = parseInt(value)
    if (isNaN(parsed) || parsed < min) return min
    if (parsed > max) return max
    return parsed
}

const totalSeconds = computed({
    get: () => clampNumber(props.modelValue ?? 0, { min: 0 }),
    set: (value) => emits('update:modelValue', clampNumber(value, { min: 0 })),
})

const hours = computed({
    get: () => Math.floor(totalSeconds.value / 3600),
    set: (value) => {
        const clamped = clampNumber(value, { min: 0 })
        const remainder = totalSeconds.value % 3600
        totalSeconds.value = clamped * 3600 + remainder
    },
})

const minutes = computed({
    get: () => Math.floor((totalSeconds.value % 3600) / 60),
    set: (value) => {
        const clamped = clampNumber(value, { min: 0, max: 59 })
        const secondsPart = totalSeconds.value % 60
        const hoursPart = Math.floor(totalSeconds.value / 3600)
        totalSeconds.value = hoursPart * 3600 + clamped * 60 + secondsPart
    },
})

const seconds = computed({
    get: () => totalSeconds.value % 60,
    set: (value) => {
        const clamped = clampNumber(value, { min: 0, max: 59 })
        const minutesPart = Math.floor((totalSeconds.value % 3600) / 60)
        const hoursPart = Math.floor(totalSeconds.value / 3600)
        totalSeconds.value = hoursPart * 3600 + minutesPart * 60 + clamped
    },
})

const DurationField = defineComponent({
    name: 'DurationField',
    props: {
        modelValue: { type: Number, default: 0 },
        suffix: { type: String, required: true },
        max: { type: Number, default: Infinity },
    },
    emits: ['update:modelValue'],
    setup(fieldProps, { emit }) {
        const onInput = (event) => {
            const parsed = parseInt(event.target.value)
            let next = isNaN(parsed) || parsed < 0 ? 0 : parsed
            if (fieldProps.max !== Infinity && next > fieldProps.max) next = fieldProps.max
            emit('update:modelValue', next)
        }
        return () =>
            h('div', { class: 'flex flex-col items-center space-y-0.5' }, [
                h('input', {
                    value: fieldProps.modelValue,
                    onInput,
                    type: 'number',
                    min: 0,
                    max: fieldProps.max,
                    inputmode: 'numeric',
                    style: 'appearance: textfield; -moz-appearance: textfield;',
                    class:
                        'w-20 rounded bg-dark-lighter/70 px-3 py-1.5 text-center text-xl font-semibold text-white outline-hidden focus:outline-hidden',
                }),
                h('span', { class: 'text-xxs uppercase text-text-gray' }, fieldProps.suffix),
            ])
    },
})
</script>
